"use client";

import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Fade,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Close,
  CloudUpload,
  Code,
  ContentCopy,
  Delete,
  Download,
  EditNote,
  InsertDriveFile,
  WarningAmber,
} from "@mui/icons-material";
import {
  getUtf8ByteLength,
  HtmlMinifyOptions,
  minifyHtml,
} from "@/lib/htmlMinifier";

interface HtmlFile {
  file: File;
  id: string;
  content: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export default function HtmlMinifierConverter() {
  const [htmlFile, setHtmlFile] = useState<HtmlFile | null>(null);
  const [htmlText, setHtmlText] = useState<string>("");
  const [minifiedOutput, setMinifiedOutput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [inputTab, setInputTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const [removeComments, setRemoveComments] = useState(true);
  const [collapseWhitespace, setCollapseWhitespace] = useState(true);
  const [aggressive, setAggressive] = useState(false);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    const isValidExtension =
      fileName.endsWith(".html") ||
      fileName.endsWith(".htm") ||
      fileName.endsWith(".xhtml") ||
      fileName.endsWith(".txt");

    if (
      isValidExtension ||
      file.type === "text/html" ||
      file.type === "application/xhtml+xml" ||
      file.type === "text/plain"
    ) {
      const content = await file.text();
      setHtmlFile({
        file,
        id: Math.random().toString(36).substr(2, 9),
        content,
      });
      setHtmlText(content);
      setError(null);
      setSuccess(false);
      setMinifiedOutput("");
      setCopied(false);
    } else {
      setError(
        `${file.name} is not a supported format. Please use .html, .htm, .xhtml, or .txt files.`
      );
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeFile = () => {
    setHtmlFile(null);
    setHtmlText("");
    setError(null);
    setSuccess(false);
    setMinifiedOutput("");
    setCopied(false);
    const fileInput = document.getElementById(
      "html-file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const clearAll = () => {
    removeFile();
    setInputTab(0);
  };

  const options: HtmlMinifyOptions = useMemo(
    () => ({
      removeComments,
      collapseWhitespace,
      removeIntertagWhitespace: aggressive,
    }),
    [removeComments, collapseWhitespace, aggressive]
  );

  const stats = useMemo(() => {
    const originalBytes = getUtf8ByteLength(htmlText);
    const minifiedBytes = getUtf8ByteLength(minifiedOutput);
    const savedBytes = Math.max(0, originalBytes - minifiedBytes);
    const savedPercent =
      originalBytes > 0 ? (savedBytes / originalBytes) * 100 : 0;

    return {
      originalBytes,
      minifiedBytes,
      savedBytes,
      savedPercent,
    };
  }, [htmlText, minifiedOutput]);

  const runMinify = () => {
    const textToMinify = htmlText;

    if (!textToMinify.trim()) {
      setError("Please enter some HTML or upload a file to minify.");
      return;
    }

    setError(null);
    setSuccess(false);
    setCopied(false);

    try {
      const result = minifyHtml(textToMinify, options);
      setMinifiedOutput(result);
      setSuccess(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to minify HTML";
      setError(message);
      setMinifiedOutput("");
    }
  };

  const copyToClipboard = async () => {
    if (!minifiedOutput) return;
    try {
      await navigator.clipboard.writeText(minifiedOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const downloadMinifiedHtml = () => {
    if (!minifiedOutput) return;
    const blob = new Blob([minifiedOutput], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const baseName =
      htmlFile?.file.name.replace(/\.(html|htm|xhtml|txt)$/i, "") || "minified";
    a.download = `${baseName}.min.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleInputTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setInputTab(newValue);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Tabs
          value={inputTab}
          onChange={handleInputTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<EditNote />} label="Paste HTML" iconPosition="start" />
          <Tab
            icon={<CloudUpload />}
            label="Upload File"
            iconPosition="start"
          />
        </Tabs>

        <TabPanel value={inputTab} index={0}>
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder={`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Example</title>
  </head>
  <body>
    <h1>Hello world</h1>
    <!-- comments can be removed -->
    <p>Paste your HTML here…</p>
  </body>
</html>`}
            value={htmlText}
            onChange={(e) => {
              setHtmlText(e.target.value);
              setError(null);
              setSuccess(false);
              if (minifiedOutput) setMinifiedOutput("");
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
        </TabPanel>

        <TabPanel value={inputTab} index={1}>
          {!htmlFile ? (
            <Card
              variant="outlined"
              sx={{
                border: "2px dashed",
                borderColor: dragActive ? "primary.main" : "divider",
                bgcolor: dragActive ? "primary.50" : "grey.50",
                transition: "all 0.2s ease",
              }}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <CardContent sx={{ textAlign: "center", py: 5 }}>
                <CloudUpload sx={{ fontSize: 70, color: "primary.main" }} />
                <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                  Drag & drop an HTML file
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Supported: .html, .htm, .xhtml, .txt
                </Typography>

                <Button
                  variant="contained"
                  component="label"
                  sx={{
                    background:
                      "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                  }}
                >
                  Choose File
                  <input
                    id="html-file-input"
                    type="file"
                    hidden
                    accept=".html,.htm,.xhtml,.txt,text/html,text/plain,application/xhtml+xml"
                    onChange={(e) => handleFileSelect(e.target.files)}
                  />
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  flexWrap="wrap"
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <InsertDriveFile color="primary" />
                    <Box>
                      <Typography fontWeight={600}>
                        {htmlFile.file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatBytes(htmlFile.file.size)}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton aria-label="Remove file" onClick={removeFile}>
                    <Delete />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Options
          </Typography>

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={removeComments}
                  onChange={(e) => setRemoveComments(e.target.checked)}
                />
              }
              label="Remove HTML comments"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={collapseWhitespace}
                  onChange={(e) => setCollapseWhitespace(e.target.checked)}
                />
              }
              label="Collapse whitespace"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aggressive}
                  onChange={(e) => setAggressive(e.target.checked)}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Aggressive: remove whitespace between tags</span>
                  <WarningAmber fontSize="small" color="warning" />
                </Stack>
              }
            />
          </Stack>

          {aggressive && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Aggressive mode can change rendering when whitespace between
              inline elements is significant (example:{" "}
              <code>{"</span> <span>"}</code>). Use with care.
            </Alert>
          )}
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 3 }}
            action={
              <IconButton
                aria-label="Close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <Close fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {success && !error && (
          <Fade in={success}>
            <Alert severity="success" sx={{ mt: 3 }}>
              HTML minified successfully.
            </Alert>
          </Fade>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<Code sx={{ color: "#ffffff" }} />}
            onClick={runMinify}
            disabled={!htmlText.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Minify HTML
          </Button>
          <Button variant="outlined" startIcon={<Delete />} onClick={clearAll}>
            Clear
          </Button>
        </Stack>

        {minifiedOutput && (
          <Fade in={!!minifiedOutput}>
            <Box sx={{ mt: 4 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Minified HTML
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatBytes(stats.originalBytes)} →{" "}
                    {formatBytes(stats.minifiedBytes)} (
                    {stats.savedPercent.toFixed(1)}% smaller)
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={copyToClipboard}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={downloadMinifiedHtml}
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    }}
                  >
                    Download
                  </Button>
                </Stack>
              </Stack>

              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: "420px",
                  overflow: "auto",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    color: "#d4d4d4",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                  }}
                >
                  {minifiedOutput}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
